/**
 * usePRDReview - Hook for managing PRD Review session lifecycle.
 *
 * Pattern: docs/design-patterns/frontend/sse-consumption.md#feature-specific-sse-event-processing
 *
 * ARCHITECTURE:
 * - React Query is the SINGLE SOURCE OF TRUTH for session data
 * - Zustand (usePRDReviewUIStore) holds only UI state (viewMode, highlights)
 * - SSE events trigger cache invalidation, not direct state mutation
 *
 * This ensures:
 * - No split-brain between frontend and backend
 * - Resilience to network latency
 * - Automatic sync on refocus/reconnect
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import * as prdReviewApi from '~/services/api/prd-review';
import { usePRDReviewUIStore } from '~/stores/prdReviewStore';
import { useSSEStore } from '~/stores/sseStore';
import { logger } from '~/utils/logger';

import type {
  GetReviewSessionResponse,
  ReviewChatMessage,
  StartReviewSessionResponse,
} from '~/types';
import type { SSEEvent } from '~/types';

// Union type for session responses (start returns 'created'/'resumed', get returns 'active'/'completed'/'expired')
type ReviewSessionResponse = StartReviewSessionResponse | GetReviewSessionResponse;

// ============================================================================
// Types
// ============================================================================

export interface UsePRDReviewOptions {
  projectId: string;
  /** Called when PRD content is updated (triggers refetch) */
  onPRDUpdate?: () => void;
}

export interface UsePRDReviewReturn {
  // Session state (from React Query - SINGLE SOURCE OF TRUTH)
  sessionId: string | null;
  chatHistory: ReviewChatMessage[];
  isAgentTyping: boolean;
  isSessionLoading: boolean;
  isSessionError: boolean;
  sessionError: Error | null;

  // Actions
  startSession: () => void; // Explicitly start the session
  sendMessage: (content: string) => void;
  updateContent: (content: string) => void;
  endSession: () => void;
  refetchSession: () => void;

  // Mutation states
  isSendingMessage: boolean;
  isUpdatingContent: boolean;

  // Pending message for optimistic UI
  pendingMessageContent: string | null;
}

// Query key factory for consistency
const queryKeys = {
  session: (projectId: string) => ['prd-review-session', projectId] as const,
};

// ============================================================================
// Hook Implementation
// ============================================================================

export function usePRDReview({ onPRDUpdate, projectId }: UsePRDReviewOptions): UsePRDReviewReturn {
  const queryClient = useQueryClient();
  const uiStore = usePRDReviewUIStore();
  const { addActiveJob, connectionStatus, eventsByProject, removeActiveJob } = useSSEStore();

  // Track active job ID for cleanup
  const activeJobIdRef = useRef<string | null>(null);

  // Track processed SSE events to avoid duplicates
  const processedEventsRef = useRef<Set<string>>(new Set());

  // Track last typing state change to avoid duplicate refetches
  const lastTypingStateRef = useRef<{ isTyping: boolean; timestamp: number } | null>(null);

  // Track session ID across the hook lifecycle.
  // Seed from cached session when available to improve reload/resume hydration.
  const [sessionId, setSessionId] = useState<string | null>(
    () =>
      queryClient.getQueryData<ReviewSessionResponse>(queryKeys.session(projectId))?.session_id ??
      null,
  );

  // Track typing state from SSE (avoids refetching session on every typing event)
  const [isTypingFromSSE, setIsTypingFromSSE] = useState<boolean>(false);

  // ---------------------------------------------------------------------------
  // Start Session Mutation - Call ONCE to initialize session
  // ---------------------------------------------------------------------------
  const startSessionMutation = useMutation({
    mutationKey: ['prd-review', 'start-session', projectId],
    mutationFn: async (): Promise<ReviewSessionResponse> => {
      logger.info({ component: 'usePRDReview' }, 'Starting review session', { projectId });
      return await prdReviewApi.startReviewSession({ project_id: projectId });
    },
    onSuccess: session => {
      // Store session ID for subsequent queries
      setSessionId(session.session_id);

      // Register active job for SSE connection
      const jobId = `prd-review-${session.session_id}`;
      activeJobIdRef.current = jobId;
      addActiveJob(jobId, session.project_id, 3600);

      // Cache session data
      queryClient.setQueryData(queryKeys.session(projectId), session);

      logger.info({ component: 'usePRDReview' }, 'Session started', {
        sessionId: session.session_id,
        messageCount: session.chat_history.length,
        isAgentEditing: session.is_agent_editing,
      });
    },
    retry: false, // Don't retry to prevent duplicate calls
    gcTime: 5 * 60 * 1000, // Keep mutation result for 5 minutes to prevent duplicate starts
  });

  // ---------------------------------------------------------------------------
  // Session Query - Fetch EXISTING session state (only after session_id is known)
  // ---------------------------------------------------------------------------
  const sessionQuery = useQuery({
    queryKey: queryKeys.session(projectId),
    queryFn: async (): Promise<ReviewSessionResponse> => {
      if (!sessionId) {
        throw new Error('Cannot fetch session without session_id');
      }

      logger.info({ component: 'usePRDReview' }, 'Fetching session state', {
        projectId,
        sessionId,
      });

      return await prdReviewApi.getReviewSession(sessionId);
    },
    enabled: !!projectId && !!sessionId, // Only fetch if we have a session ID
    initialData: () =>
      queryClient.getQueryData<ReviewSessionResponse>(queryKeys.session(projectId)),
    staleTime: 2000, // 2 seconds - batches rapid SSE invalidations into single refetch
    refetchOnWindowFocus: true, // Sync when user returns to tab
    retry: 2,
  });

  // ---------------------------------------------------------------------------
  // Explicit session start function (called by component)
  // ---------------------------------------------------------------------------
  const startSession = useCallback(() => {
    // Don't start if already starting or have a session
    if (startSessionMutation.isPending || sessionId) {
      return;
    }

    // Check cache first
    const cachedSession = queryClient.getQueryData<ReviewSessionResponse>(
      queryKeys.session(projectId),
    );
    if (cachedSession?.session_id) {
      setSessionId(cachedSession.session_id);
      // Re-register SSE job if needed
      if (!activeJobIdRef.current) {
        const jobId = `prd-review-${cachedSession.session_id}`;
        activeJobIdRef.current = jobId;
        addActiveJob(jobId, cachedSession.project_id, 3600);
      }
      return;
    }

    // Start new session
    startSessionMutation.mutate();
  }, [sessionId, projectId, queryClient, startSessionMutation, addActiveJob]);

  // Derived state from query
  const session = sessionQuery.data;
  // Memoize chatHistory to prevent creating a new array reference on every render
  // This is important for useEffect dependencies that check chatHistory
  const chatHistory = useMemo(() => session?.chat_history ?? [], [session?.chat_history]);

  // Typing state: SSE primary, DB fallback (prevents race conditions)
  const isAgentTyping = ((): boolean => {
    const isConnected = connectionStatus === 'connected';

    // If SSE connected, trust it exclusively (fast, real-time)
    if (isConnected) {
      return isTypingFromSSE;
    }

    // If SSE disconnected, fall back to DB state (degraded but functional)
    return session?.is_agent_editing || false;
  })();

  // ---------------------------------------------------------------------------
  // Client-Side Safety Timeout - Force clear stuck typing indicator
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!isTypingFromSSE) return;

    // If agent typing for > 3 minutes, force clear (backend timeout is 2min + 1min buffer)
    // Protects against agent crash/hang where isTyping:false SSE event never arrives
    const timeout = setTimeout(() => {
      logger.warn(
        { component: 'usePRDReview' },
        'Agent typing timeout - forcing clear after 3 minutes',
        { projectId, sessionId },
      );
      setIsTypingFromSSE(false);
      // Sync with server state (backend should have cleared stale lock by now)
      void queryClient.invalidateQueries({ queryKey: queryKeys.session(projectId) });
    }, 180_000); // 3 minutes

    return (): void => clearTimeout(timeout);
  }, [isTypingFromSSE, projectId, sessionId, queryClient]);

  // ---------------------------------------------------------------------------
  // Clear Pending Message When Confirmed in Chat History
  // ---------------------------------------------------------------------------
  // This prevents the Draft Answer button from flickering. We keep pendingMessage
  // set until we see the message in chatHistory (via refetch), ensuring the button
  // stays hidden during the race condition window between mutation completion and
  // session refetch.
  useEffect(() => {
    const pendingContent = uiStore.pendingMessageContent;
    if (
      pendingContent &&
      chatHistory.some(msg => msg.role === 'user' && msg.content === pendingContent)
    ) {
      uiStore.setPendingMessage(null);
    }
  }, [chatHistory, uiStore]);

  // ---------------------------------------------------------------------------
  // Send Message Mutation
  // ---------------------------------------------------------------------------
  const sendMessageMutation = useMutation({
    mutationKey: ['prd-review', 'send-message', sessionId],
    mutationFn: async (content: string) => {
      if (!sessionId) {
        throw new Error('No active session');
      }
      return prdReviewApi.sendReviewMessage({
        session_id: sessionId,
        content,
      });
    },
    onMutate: (content: string) => {
      // Set pending message for optimistic UI (shows "sending..." state)
      uiStore.setPendingMessage(content);
      // Clear any highlight from previous agent response
      uiStore.setLastChangedSection(null);
    },
    onSuccess: () => {
      logger.info({ component: 'usePRDReview' }, 'Message sent, invalidating session cache');
      // Invalidate session to refetch with new message
      void queryClient.invalidateQueries({ queryKey: queryKeys.session(projectId) });
    },
    onError: error => {
      const apiError = error as { status?: number };
      if (apiError.status === 409) {
        // Agent is already processing - sync state
        logger.info({ component: 'usePRDReview' }, 'Agent busy (409), refetching session');
        void queryClient.invalidateQueries({ queryKey: queryKeys.session(projectId) });
      } else {
        logger.error(
          { component: 'usePRDReview' },
          'Failed to send message',
          error instanceof Error ? error : new Error(String(error)),
        );
      }
    },
    onSettled: (_data, error) => {
      // Only clear pending message on error - on success, we wait for the message
      // to appear in chatHistory (via refetch) before clearing. This prevents a race
      // condition where pendingMessage is cleared but isAgentTyping hasn't been
      // updated yet (causing the Draft Answer button to briefly reappear).
      if (error) {
        uiStore.setPendingMessage(null);
      }
    },
  });

  // ---------------------------------------------------------------------------
  // Update Content Mutation
  // ---------------------------------------------------------------------------
  const updateContentMutation = useMutation({
    mutationKey: ['prd-review', 'update-content', sessionId],
    mutationFn: async (content: string) => {
      if (!sessionId) {
        throw new Error('No active session');
      }
      return prdReviewApi.updatePRDContent({
        session_id: sessionId,
        content,
      });
    },
    onSuccess: () => {
      // Invalidate PRD document cache to show updated content
      void queryClient.invalidateQueries({ queryKey: ['prd-document', projectId] });
      logger.info({ component: 'usePRDReview' }, 'PRD content updated');
    },
    onError: async error => {
      logger.error(
        { component: 'usePRDReview' },
        'Failed to update content',
        error instanceof Error ? error : new Error(String(error)),
      );

      // On 409 (agent editing conflict), refresh session state
      const apiError = error as { status?: number };
      if (apiError.status === 409) {
        void queryClient.invalidateQueries({ queryKey: queryKeys.session(projectId) });
      }
    },
  });

  // ---------------------------------------------------------------------------
  // End Session
  // ---------------------------------------------------------------------------
  const endSession = useCallback(async () => {
    if (!sessionId) return;

    try {
      await prdReviewApi.endReviewSession(sessionId);

      // Remove active job to close SSE connection
      if (activeJobIdRef.current) {
        removeActiveJob(activeJobIdRef.current);
        activeJobIdRef.current = null;
      }

      // Clear React Query cache for this session
      queryClient.removeQueries({ queryKey: queryKeys.session(projectId) });

      // Clear session ID state
      setSessionId(null);

      // Reset UI store
      uiStore.reset();

      logger.info({ component: 'usePRDReview' }, 'Session ended');
    } catch (error) {
      logger.error(
        { component: 'usePRDReview' },
        'Failed to end session',
        error instanceof Error ? error : new Error(String(error)),
      );
    }
  }, [sessionId, projectId, queryClient, removeActiveJob, uiStore]);

  // ---------------------------------------------------------------------------
  // SSE Event Handler - Updates state directly from events (no refetch needed)
  // ---------------------------------------------------------------------------
  const handleSSEEvent = useCallback(
    (event: SSEEvent) => {
      if (!sessionId) return;

      switch (event.event) {
        case 'prd_review_typing': {
          if (event.data.sessionId === sessionId) {
            // Update typing indicator directly from event (no refetch needed)
            const isTyping = event.data.isTyping === true;
            setIsTypingFromSSE(isTyping);

            // When agent stops typing, refetch session to sync is_agent_editing flag
            // Deduplicate: only refetch if this is a new state change (not a duplicate event)
            const now = Date.now();
            const lastState = lastTypingStateRef.current;
            const isDuplicateEvent =
              lastState && lastState.isTyping === isTyping && now - lastState.timestamp < 1000; // Within 1 second

            if (!isDuplicateEvent) {
              lastTypingStateRef.current = { isTyping, timestamp: now };

              // On typing stop, refetch to sync server state (non-urgent background sync)
              // The optimistic update in prd_review_message handler already cleared the UI
              if (!isTyping) {
                void queryClient.refetchQueries({ queryKey: queryKeys.session(projectId) });
              }
            }

            if (event.data.activity) {
              logger.debug({ component: 'usePRDReview' }, 'Agent activity', {
                activity: event.data.activity,
              });
            }
          }
          break;
        }

        case 'prd_review_message': {
          if (event.data.sessionId === sessionId) {
            logger.info({ component: 'usePRDReview' }, 'SSE: Agent message received');
            // Clear typing indicator (agent finished typing)
            setIsTypingFromSSE(false);

            // Optimistically update cache: agent is done editing
            // This prevents race condition where refetch happens before backend updates is_agent_editing
            queryClient.setQueryData<ReviewSessionResponse>(queryKeys.session(projectId), old => {
              if (!old) return old;
              return {
                ...old,
                is_agent_editing: false,
              };
            });

            // Ensure agent response is pulled even if typing-stop SSE is missing or deduplicated.
            void queryClient.invalidateQueries({ queryKey: queryKeys.session(projectId) });
          }
          break;
        }

        case 'prd_review_update': {
          if (event.data.sessionId === sessionId) {
            logger.info({ component: 'usePRDReview' }, 'SSE: PRD updated', {
              changeSummary: event.data.changeSummary,
            });

            // Set highlight first so the user sees what changed; then refetch after a short
            // delay so the preview DOM isn't replaced immediately (which would remove the highlight).
            if (event.data.changeSummary) {
              uiStore.setLastChangedSection(event.data.changeSummary);
            }

            window.setTimeout(() => {
              void queryClient.invalidateQueries({
                queryKey: ['prd-document', projectId],
                refetchType: 'all',
              });
              onPRDUpdate?.();
            }, 400);
          }
          break;
        }

        case 'prd_review_error': {
          if (event.data.sessionId === sessionId) {
            logger.error(
              { component: 'usePRDReview' },
              'SSE: Agent error',
              new Error(event.data.errorMessage),
            );
            // Clear typing indicator (agent stopped due to error)
            setIsTypingFromSSE(false);
            // Error means state may have changed - refetch to get current state
            void queryClient.invalidateQueries({ queryKey: queryKeys.session(projectId) });
          }
          break;
        }
      }
    },
    [sessionId, projectId, queryClient, onPRDUpdate, uiStore],
  );

  // ---------------------------------------------------------------------------
  // Process SSE Events from Store
  // ---------------------------------------------------------------------------
  useEffect(() => {
    // Don't process events until session is loaded - otherwise we'd mark events
    // as processed before we can actually handle them, causing them to be lost
    if (!sessionId) return;

    const projectEvents = eventsByProject[projectId] || [];

    for (const event of projectEvents) {
      // Skip if already processed
      if (event.id && processedEventsRef.current.has(event.id)) {
        continue;
      }

      // Only process PRD review events
      if (
        event.event === 'prd_review_typing' ||
        event.event === 'prd_review_message' ||
        event.event === 'prd_review_update' ||
        event.event === 'prd_review_error'
      ) {
        handleSSEEvent(event);

        if (event.id) {
          processedEventsRef.current.add(event.id);
        }
      }
    }
  }, [eventsByProject, projectId, handleSSEEvent, sessionId]);

  // ---------------------------------------------------------------------------
  // Cleanup on Unmount
  // ---------------------------------------------------------------------------
  useEffect(() => {
    // Capture refs for cleanup (React hooks exhaustive-deps rule)
    const jobIdRef = activeJobIdRef;
    const eventsRef = processedEventsRef;

    return (): void => {
      // Remove active job when hook unmounts
      if (jobIdRef.current) {
        removeActiveJob(jobIdRef.current);
        jobIdRef.current = null;
      }

      // Reset UI store - use getState() to avoid subscription-based updates during cleanup
      // This prevents infinite re-render loop when cleanup triggers state change
      usePRDReviewUIStore.getState().reset();

      // Clear processed events
      eventsRef.current.clear();

      logger.info({ component: 'usePRDReview' }, 'Cleaned up on unmount');
    };
  }, [removeActiveJob]);

  // ---------------------------------------------------------------------------
  // Return
  // ---------------------------------------------------------------------------
  return {
    // Session state (from React Query)
    sessionId,
    chatHistory,
    isAgentTyping,
    // Include both session start and session fetch loading states
    isSessionLoading: startSessionMutation.isPending || sessionQuery.isLoading,
    isSessionError: startSessionMutation.isError || sessionQuery.isError,
    sessionError: startSessionMutation.error || sessionQuery.error,

    // Actions
    startSession, // Explicit function to start session (no useEffect antipattern)
    sendMessage: (content: string) => sendMessageMutation.mutate(content),
    updateContent: (content: string) => updateContentMutation.mutate(content),
    endSession: () => void endSession(),
    refetchSession: () =>
      void queryClient.invalidateQueries({ queryKey: queryKeys.session(projectId) }),

    // Mutation states
    isSendingMessage: sendMessageMutation.isPending,
    isUpdatingContent: updateContentMutation.isPending,

    // Pending message for optimistic UI
    pendingMessageContent: uiStore.pendingMessageContent,
  };
}
